import { createAction } from '@reduxjs/toolkit';
import { TDOId } from '@common-modules/universal/models/Brands';
import {
  createGraphQLFailureAction,
  createGraphQLSuccessAction,
} from '@helpers/callGraphQLApi';
import {
  ActiveTab,
  AppliedLexiconRecord,
  BoundingBox,
  Detection,
  DetectionRectKey,
  DetectionSource,
  DocumentComment,
  IntegrationAuth,
  IntegrationKey,
  IntegrationState,
  KeywordMetrics,
  OcrManifest,
  PersistedPiiMatch,
  BatesStampSettings,
  PersistedRedactionState,
  PiiDetection,
  PiiMetrics,
  PiiTypeId,
  Redaction,
  RedactedVersion,
  RedactionCodeAssignment,
  TextSelectMetrics,
  Tool,
  VendorRequestIds,
  AppliedWatermark,
  ZoomMode,
} from './models';
import type {
  FetchDocumentCommentsResponse,
  UpdateDocumentCommentResponse,
} from './services';
import type { StatusHistoryNoteOutcome } from '@common-modules/statusHistory/models';

/**
 * Action vocabulary for the documents slice.
 *
 * Convention: `redact/documents/<VERB_NOUN>` namespace.
 * Triplets `[REQUEST, SUCCESS, FAILURE]` for I/O via createGraphQLRequestAction
 * factories live in `services.ts`; the simple action set below covers
 * editor mutations.
 */

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/** Search state carried across the OCR reload's session rebuild. */
export interface KeywordSearchSeed {
  phrase: string;
  matchCase: boolean;
  wholeWord: boolean;
}

export const OPEN_DOCUMENT = createAction<{
  tdoId: TDOId;
  keywordSearchSeed?: KeywordSearchSeed;
  /**
   * Where the reviewer was, carried across an OCR reload.
   *
   * The reload rebuilds the session from scratch, which used to land them at
   * the top of the document — on a long scan, the cost of taking the offer was
   * losing your place, which is most of the reason not to take it.
   */
  viewSeed?: {
    page: number;
    zoom?: number;
    /**
     * Carried only when the outgoing session had PII settled at `done`.
     *
     * A reload rebuilds the session, so PII returns to `checking` and the panel
     * says it is loading results it had a moment ago — work the reviewer
     * watched finish, apparently undone. The detections themselves come back
     * with the re-open (they are on the saved asset), so the state is the only
     * thing that needs carrying; the poller still re-resolves behind it and
     * will correct this if it turns out to be wrong.
     */
    piiState?: 'done';
  };
}>('redact/documents/OPEN_DOCUMENT');
export const OPEN_DOCUMENT_SUCCESS = createAction<{
  tdoId: TDOId;
  documentName: string;
  pdfUrl: string;
  /**
   * The `media` asset `pdfUrl` was signed from. Pinned for the session, except
   * on the restore path, which adopts it — see `SessionState.mediaAssetId`.
   */
  mediaAssetId?: string | null;
  /**
   * The engine that produced that asset, or null for the uploaded original.
   * Answers "is the file being opened OCR's own output?" — see
   * `SessionState.mediaAssetEngineId`.
   */
  mediaAssetEngineId?: string | null;
  totalPages: number;
  detections: PiiDetection[];
  savedState?: PersistedRedactionState | null;
  /**
   * Existing `redacted-document` assets on the TDO, mapped to RedactedVersion
   * entries so the Redacted Files tab shows prior burn-ins when the user
   * reopens a document. The ticket policy is "remove all older" on each
   * burn-in (see exportPush saga), so in practice this is at most one
   * entry today.
   */
  versionHistory?: RedactedVersion[];
  /**
   * createdDateTime of the most-recent `document-redaction-data` asset, if any.
   * Used to hydrate the editor footer's "Last saved" indicator on reopen
   * — without this, the indicator would read "—" until the next save fires.
   */
  lastSavedAt?: string | null;
  /**
   * VE-26422: the output version the newest save was sent to produce; `null`
   * when it was an ordinary save never sent for burn-in, and absent when the
   * asset predates labelling.
   */
  lastSavedBurnInVersion?: number | null;
  /**
   * Initial redaction status identity read from the TDO's `details.tags` under
   * `IN_REDACTION_TAG_KEY` (VE-21914): `redactionStatusKey ?? redactionStatus`.
   * Canonical value (built-in label like `'draft'` or a custom `statusKey`
   * UUID). `null` falls through to the default in the reducer.
   */
  redactionStatus?: string | null;
  /**
   * Human-readable name frozen on the tag's `redactionStatus` (VE-24546). Kept
   * so a deleted custom status still shows its last-known name instead of
   * "Unknown". `null` when the tag carried no name.
   */
  redactionStatusName?: string | null;
  /**
   * Per-match PII records from the saved asset (VE-21915). Stashed in session
   * and re-applied to engine detections (which load asynchronously via
   * PII_RESULTS_LOADED). Empty when none were saved.
   */
  piiMatches?: PersistedPiiMatch[];
  /**
   * Vendor "Send to" request-ids read from the TDO's `details` (VE-23442).
   * Each non-null id enables the matching button on the Redacted File tab.
   */
  vendorRequestIds?: VendorRequestIds;
  /** Parent folder (case) id/name for the generic external-integration send. */
  folderId?: string | null;
  folderName?: string | null;
  /** Forwarded from `OPEN_DOCUMENT` when this open is the OCR reload. */
  keywordSearchSeed?: KeywordSearchSeed;
  /** Likewise — the page, zoom and settled PII state to restore. */
  viewSeed?: { page: number; zoom?: number; piiState?: 'done' };
}>('redact/documents/OPEN_DOCUMENT_SUCCESS');
export const OPEN_DOCUMENT_FAILURE = createAction<{ message: string }>(
  'redact/documents/OPEN_DOCUMENT_FAILURE'
);

export const CLOSE_DOCUMENT = createAction('redact/documents/CLOSE_DOCUMENT');

// ---------------------------------------------------------------------------
// View / Tool
// ---------------------------------------------------------------------------

export const SCROLL_TO_PAGE = createAction<{
  page: number;
  /**
   * Optional vertical target within the page, in PDF user-space points
   * (top-left origin) — e.g. a redaction/detection's `y`. When set, the
   * canvas centers the viewport on that point instead of snapping the page
   * top to the top of the viewport, so a low item lands in view.
   */
  y?: number;
  /**
   * Placement of `y` in the viewport. `'center'` (default) centers it;
   * `'nearest'` minimally scrolls so the target lands ~1/3 up from the bottom
   * only when outside a comfortable band (keyword prev/next). See
   * SessionState.scrollAlign.
   */
  align?: 'center' | 'nearest';
}>('redact/documents/SCROLL_TO_PAGE');
export const SET_ZOOM = createAction<{ zoom: number; mode?: ZoomMode }>(
  'redact/documents/SET_ZOOM'
);
export const SET_TOOL = createAction<{ tool: Tool }>(
  'redact/documents/SET_TOOL'
);
export const SET_TAB = createAction<{ tab: 'editor' | 'redacted-file' }>(
  'redact/documents/SET_TAB'
);
/**
 * VE-24947 — the reviewer answered the "redaction complete" notice, either
 * way. Viewing the file is an ordinary SET_TAB; this only clears the notice.
 */
export const DISMISS_REDACTION_COMPLETE_NOTICE = createAction(
  'redact/documents/DISMISS_REDACTION_COMPLETE_NOTICE'
);
/**
 * Apply a watermark to the current document (VE-24693). Payload is a complete
 * `AppliedWatermark` (instanceId generated at the call site via uuid, like
 * CREATE_MANUAL_REDACTION) — a snapshot copied from a library definition or an
 * ad-hoc watermark. Undoable + marks the redaction state dirty (auto-saves).
 *
 * `meta.auto` says the mark arrived with NO operator action — the library's
 * auto-apply stamping a never-saved document on open (`watermarkAutoApply`).
 * Such a mark is added and undoable like any other, but does NOT dirty the
 * session: see the reducer for why, and the saga's header for what that costs.
 * It lives in `meta` rather than the payload because the payload is the mark
 * itself, and the mark is what gets written into `session.watermarks` and
 * persisted — a provenance flag has no business ending up in the saved asset.
 */
export const ADD_WATERMARK = createAction(
  'redact/documents/ADD_WATERMARK',
  (payload: AppliedWatermark, meta?: { auto?: boolean }) => ({ payload, meta })
);
/** Remove an applied watermark by instance id (VE-24693). Undoable. */
export const REMOVE_WATERMARK = createAction<{ instanceId: string }>(
  'redact/documents/REMOVE_WATERMARK'
);
/**
 * Patch a single applied watermark (VE-24693). Partial merge so the panel can
 * push one field (opacity, color, text, layout, …) and the canvas overlay can
 * push `{ placement:'free', x, y, width, height }` on drag/resize-end.
 */
export const UPDATE_WATERMARK = createAction<{
  instanceId: string;
  patch: Partial<AppliedWatermark>;
}>('redact/documents/UPDATE_WATERMARK');
/**
 * Select an applied watermark (VE-24693), or clear with `null`.
 *
 * Shared state on purpose: the panel's list and the canvas handles are two
 * views of one selection. Not undoable, not dirtying.
 */
export const SELECT_WATERMARK = createAction<{ instanceId: string | null }>(
  'redact/documents/SELECT_WATERMARK'
);

/**
 * Apply or replace the document's Bates stamping (VE-24447). One action rather
 * than an add/update pair: a document has at most one Bates sequence, so
 * "apply" and "edit" are the same operation on the same slot.
 *
 * `previous` is the stamp the document carried at the instant of the commit,
 * and it is REQUIRED so the compiler guarantees every dispatcher supplies it.
 * It is what the audit watcher needs and the one thing no reducer output
 * carries: the reducer no-ops an unchanged commit, so nothing downstream can
 * tell "applied" from "nothing happened" afterwards. An earlier version had the
 * saga remember it instead, which went stale the moment an undo changed the
 * stamp without telling the saga — and produced both a removal event for a
 * document that had none and silence for a real re-application. Read from live
 * state at the dispatch site, it cannot drift.
 */
export const SET_BATES_STAMP = createAction<{
  settings: BatesStampSettings;
  previous: BatesStampSettings | null;
}>('redact/documents/SET_BATES_STAMP');

/**
 * Switch the document's Bates numbering off (VE-24447). `previous` as above.
 * The settings are kept (`SessionState.batesStampOff`) so turning it back on
 * restores them: `keep` when given — what the panel was showing — else what
 * was applied. Also how settings edited while already off are saved.
 */
export const CLEAR_BATES_STAMP = createAction<{
  previous: BatesStampSettings | null;
  keep?: BatesStampSettings;
}>('redact/documents/CLEAR_BATES_STAMP');

/**
 * Open a watermark PREVIEW session (VE-24693): copy the document's marks into
 * `watermarkPreview`, where every subsequent add / update / remove lands until
 * the panel commits or discards.
 *
 * The panel dispatches this when it opens. Nothing else needs to know: the
 * reducer redirects the three mutating actions for as long as a preview exists,
 * so the canvas overlay goes on dispatching what it always dispatched.
 */
export const START_WATERMARK_PREVIEW = createAction(
  'redact/documents/START_WATERMARK_PREVIEW'
);

/**
 * Commit the on/off switch to the document on its own, without the preview.
 *
 * The switch applies immediately in BOTH directions rather than waiting for
 * Apply. It is the one control on that panel that is not a setting — it is the
 * feature being on or off — and a toggle that needs a second, separate
 * confirmation to take effect is not a toggle. Off proved it first: the panel's
 * controls go inert the moment it moves, leaving the reviewer holding a switch
 * whose effect they then had to Apply from a panel that had just greyed itself
 * out.
 *
 * Deliberately narrower than COMMIT_WATERMARK_PREVIEW: it writes the flag and
 * nothing else, so unapplied mark edits sitting in the preview stay unapplied.
 * A reviewer who edits a mark, switches off, and switches back on still has
 * their edit, still unapplied.
 */
export const SET_WATERMARKS_OFF = createAction<{ off: boolean }>(
  'redact/documents/SET_WATERMARKS_OFF'
);

/**
 * Apply the preview to the document and end it — the panel's Apply button. With
 * the switch off this commits an EMPTY list, which is how the toggle removes
 * every mark.
 */
export const COMMIT_WATERMARK_PREVIEW = createAction(
  'redact/documents/COMMIT_WATERMARK_PREVIEW'
);

/** Discard the preview and end it: Cancel, Esc, and clicking away. */
export const CLEAR_WATERMARK_PREVIEW = createAction(
  'redact/documents/CLEAR_WATERMARK_PREVIEW'
);

/**
 * Show Bates settings on the page WITHOUT applying them (VE-24447).
 *
 * The panel dispatches this as the operator types and clicks; the two actions
 * above fire only on Done. Nothing here dirties the session, pushes an undo
 * entry, or reaches the export — see `BatesStampPreview`.
 */
export const SET_BATES_PREVIEW = createAction<{
  settings: BatesStampSettings;
  enabled: boolean;
}>('redact/documents/SET_BATES_PREVIEW');

/**
 * End the preview session, putting the page back to whatever the document
 * itself carries. Dispatched on every exit from the panel — Done included,
 * where it runs alongside the SET/CLEAR that commits.
 */
export const CLEAR_BATES_PREVIEW = createAction(
  'redact/documents/CLEAR_BATES_PREVIEW'
);

/**
 * Fill in the DRAWN image for watermarks restored from an asset (VE-24693).
 *
 * The asset stores only the inputs — colour base plus the black-&-white and
 * colour choices — because the drawn form is a pure function of them and
 * persisting both doubled the payload and gave the export a second source of
 * truth. This carries the derivation back in after the document opens.
 *
 * Deliberately NOT undoable and NOT dirtying: nothing the user did happened
 * here. Routing it through UPDATE_WATERMARK would mark a freshly-opened
 * document dirty, start the autosave clock and burn an undo slot.
 */
export const HYDRATE_WATERMARK_IMAGES = createAction<{
  images: Array<{
    instanceId: string;
    imageDataUrl: string;
    /**
     * The raster fetched back from the mark's `document-watermark-image` asset
     * (format 1.3.0). Optional: a 1.2.0 body carries its base inline, so that
     * mark already has one and only the drawn form is missing.
     */
    imageBaseDataUrl?: string;
  }>;
  /**
   * WHY this raster arrived, which decides where it lands while a watermark
   * preview is open — the two dispatchers mean opposite things:
   *
   * `'restore'` — `openDocument` fetched the mark's stored artwork. It belongs
   * to the DOCUMENT, so it is written to the session (and mirrored into the
   * preview's copy of the same mark). Writing it to the preview alone meant
   * that opening the panel before the fetch landed and then cancelling threw
   * the artwork away for good, and the mark exported with nothing to draw.
   *
   * `'edit'` — `watermarkDerive` redrew a mark because the operator changed its
   * grayscale or tint. That change lives in the preview and has not been
   * accepted, so its raster must not touch the committed mark; writing it there
   * left a cancelled edit on the document for the export to burn.
   */
  origin: 'restore' | 'edit';
  /**
   * Marks the restore could NOT resolve, so the panel can say so (VE-28770).
   *
   * Carried on the same action as the successes because they are one answer to
   * one question — "what did this open manage to read?" — and splitting them
   * would let a session record the successes and miss the failures. Only
   * meaningful for `'restore'`; an `'edit'` redraw has no such category.
   */
  failedInstanceIds?: string[];
}>('redact/documents/HYDRATE_WATERMARK_IMAGES');

/**
 * Record the `document-watermark-image` asset an image mark's raster was just
 * stored in (VE-24693 §J).
 *
 * Dirtying but NOT undoable, and the pairing is deliberate. It must dirty the
 * document, because until this id reaches a save the persisted mark has no
 * artwork at all — the raster is no longer inline, so a body written before the
 * upload lands references nothing. It must not be undoable, because the upload
 * is a consequence of the user's action, not a step of it: an undo should remove
 * the watermark they added, not walk back to a half-stored version of it.
 *
 * `meta.auto` is carried over from the `ADD_WATERMARK` that started the upload
 * (VE-30011): an auto-applied mark's id does not dirty a never-saved, clean
 * document — see the reducer for why that is safe.
 */
export const SET_WATERMARK_IMAGE_ASSET = createAction(
  'redact/documents/SET_WATERMARK_IMAGE_ASSET',
  (
    payload: { instanceId: string; imageAssetId: string },
    meta?: { auto?: boolean }
  ) => ({ payload, meta })
);

/**
 * Toggle watermark edit mode (VE-24693) — set true while the watermark panel is
 * open so the canvas overlay enables drag/resize (AC#7). Not undoable/dirtying.
 */
export const SET_WATERMARK_EDITING = createAction<{ editing: boolean }>(
  'redact/documents/SET_WATERMARK_EDITING'
);
/**
 * VE-24947 (U2-FR2) — the reviewer picked another document in the Documents
 * panel. Suspends the outgoing document (keeping its unsaved work) before the
 * route change that opens the next one.
 *
 * Distinct from CLOSE_DOCUMENT, which means "leaving the editor" and discards
 * every open session. Switching is movement WITHIN a task; closing ends it.
 *
 * NOT DISPATCHED IN PRODUCTION, on purpose. A real switch suspends inline in
 * OPEN_DOCUMENT_SUCCESS, so the park and the swap land in ONE action and the
 * chrome never sees a null session — dispatching this first nulled it and put
 * the page behind a full-screen spinner (see DocumentEditor's
 * `onSwitchDocument`). It stays because it drives the same `suspendIfWorthKeeping`
 * the switch uses, which lets the reducer tests exercise parking on its own
 * rather than only as half of an open. Do not reach for it in app code.
 */
export const SUSPEND_ACTIVE_DOCUMENT = createAction(
  'redact/documents/SUSPEND_ACTIVE_DOCUMENT'
);

/**
 * VE-24947 — a switch to another document has started.
 *
 * Records where the editor is heading WITHOUT tearing down what is on screen,
 * so the chrome survives the switch and only the document panes reload. The
 * actual swap happens atomically in OPEN_DOCUMENT_SUCCESS; this only marks the
 * journey. `documentName` comes from the Documents panel row, which knows it
 * before the server answers.
 */
export const SWITCH_DOCUMENT_STARTED = createAction<{
  tdoId: TDOId;
  documentName: string;
  /** Tab to land on — the one the switch was made from. */
  activeTab: ActiveTab;
  /** Whether the incoming document already has a redacted output. */
  hasRedactedOutput: boolean;
}>('redact/documents/SWITCH_DOCUMENT_STARTED');

/**
 * VE-24947 — open/close the editor's Documents panel. Survives a document
 * switch on purpose; see `DocumentsStore.documentsPanelOpen`.
 */
export const SET_DOCUMENTS_PANEL_OPEN = createAction<boolean>(
  'redact/documents/SET_DOCUMENTS_PANEL_OPEN'
);
/**
 * VE-29142 — the editor opened the Documents panel on the reviewer's behalf,
 * rather than the reviewer opening it.
 *
 * Same state change, deliberately NOT the same action: the preferences saga
 * persists the panel's open state per request, and an automatic open is not a
 * preference. Landing once on a redacted-file tab with no output — which opens
 * the panel so there is something to switch to — would otherwise rewrite that
 * request's stored "panel closed" choice permanently.
 */
export const AUTO_OPEN_DOCUMENTS_PANEL = createAction(
  'redact/documents/AUTO_OPEN_DOCUMENTS_PANEL'
);

/**
 * VE-29142 — apply a request's remembered panel layout, read from localStorage
 * when the editor opens. One action for all three so the panels settle in a
 * single render rather than shuffling into place.
 */
export const HYDRATE_PANEL_PREFERENCES = createAction<{
  documentsPanelOpen: boolean;
  documentsPanelPinned: boolean;
  pagesPanelCollapsed: boolean;
  /**
   * Read from a DOCUMENT-keyed store, unlike the three above, which belong to
   * the request. Carried on the same action anyway: both are read at the same
   * moment by the same saga, and one action means the panels settle in a single
   * render rather than shuffling into place.
   */
  toolPanelExpanded: boolean;
}>('redact/documents/HYDRATE_PANEL_PREFERENCES');

/**
 * Show or hide the tool rail's button names, widening the rail to fit them.
 *
 * Remembered per window band — see `readToolPanelExpanded`. Its own action
 * rather than a field on a general panel action, so the preferences saga can
 * watch it and write to the band-keyed store without touching the request's.
 */
export const SET_TOOL_PANEL_EXPANDED = createAction<boolean>(
  'redact/documents/SET_TOOL_PANEL_EXPANDED'
);

/** VE-24947 — pin the Documents panel open, so it stops collapsing on mouse-out. */
export const SET_DOCUMENTS_PANEL_PINNED = createAction<boolean>(
  'redact/documents/SET_DOCUMENTS_PANEL_PINNED'
);

/**
 * VE-24947 — save every open document that has unsaved work: the one on screen
 * and any suspended by a switch. Answered by SAVE_ALL_SETTLED.
 */
export const SAVE_ALL_DOCUMENTS = createAction(
  'redact/documents/SAVE_ALL_DOCUMENTS'
);

/**
 * VE-24947 — the save-all pass finished. `failed` names the documents whose
 * save did not land, so the caller can report them rather than assume success.
 */
export const SAVE_ALL_SETTLED = createAction<{ failed: TDOId[] }>(
  'redact/documents/SAVE_ALL_SETTLED'
);

/**
 * VE-24947 (U2-FR5) — the reviewer dragged a document into a new position.
 * Carries the WHOLE resulting order, not a from/to pair, so the reducer stores
 * a fact rather than replaying a gesture.
 */
export const SET_DOCUMENT_ORDER = createAction<{
  folderId: string;
  order: TDOId[];
}>('redact/documents/SET_DOCUMENT_ORDER');

/** VE-24947 — show/hide the Pages panel. Slice-level so it survives a document switch. */
export const SET_PAGES_PANEL_COLLAPSED = createAction<boolean>(
  'redact/documents/SET_PAGES_PANEL_COLLAPSED'
);

export const SET_DETECTIONS_TAB = createAction<{
  tab: 'detections' | 'comments';
}>('redact/documents/SET_DETECTIONS_TAB');
export const SET_REDACTIONS_SUBTAB = createAction<{
  subtab: 'all' | 'pii' | 'drawn' | 'text-select' | 'keyword';
}>('redact/documents/SET_REDACTIONS_SUBTAB');

// ---------------------------------------------------------------------------
// Detections
// ---------------------------------------------------------------------------

export const TOGGLE_DETECTION = createAction<{ detectionId: string }>(
  'redact/documents/TOGGLE_DETECTION'
);
/**
 * Redact or unredact SEVERAL detections as one step, for the multi-select
 * bar. Not a loop of TOGGLE_DETECTION: that pushed an undo snapshot each time,
 * so one bulk click cost N presses of ctrl-Z to reverse and a big selection
 * pushed the rest of the history off the end of UNDO_STACK_LIMIT (VE-28716).
 * Absolute rather than toggling, so a mixed selection lands in one state
 * instead of each row flipping to its own opposite.
 */
export const SET_DETECTIONS_REDACTED = createAction<{
  detectionIds: string[];
  redacted: boolean;
}>('redact/documents/SET_DETECTIONS_REDACTED');
export const TOGGLE_DETECTION_PAGE = createAction<{
  detectionId: string;
  page: number;
}>('redact/documents/TOGGLE_DETECTION_PAGE');
// Redact / unredact a single PII match (one occurrence) within a group,
// keyed by its vendor match id. Drives the overlay menu's per-match
// redact/unredact on detections that carry vendor ids.
export const TOGGLE_DETECTION_MATCH = createAction<{
  detectionId: string;
  matchId: string;
}>('redact/documents/TOGGLE_DETECTION_MATCH');
// Delete a single PII match (one occurrence) from the Redactions list, keyed
// by its vendor match id. The detection stays listed while other matches
// remain; deleting the last live match removes the detection entirely.
export const DELETE_DETECTION_MATCH = createAction<{
  detectionId: string;
  matchId: string;
}>('redact/documents/DELETE_DETECTION_MATCH');
export const TOGGLE_DETECTION_EXPANDED = createAction<{ detectionId: string }>(
  'redact/documents/TOGGLE_DETECTION_EXPANDED'
);
// Remove PII detections from the Redactions list entirely (the "Delete"
// action). Clears `addedToRedactions` + `checked` so the detection drops out
// of the Redactions list and reverts to a candidate on the PII tab.
export const REMOVE_DETECTIONS_FROM_REDACTIONS = createAction<{
  detectionIds: string[];
}>('redact/documents/REMOVE_DETECTIONS_FROM_REDACTIONS');
/**
 * Take a lexicon off the open document (VE-24948 / U5).
 *
 * Removes the applied RECORD and every match the reviewer never acted on.
 *
 * REDACTED MATCHES SURVIVE, on their own cards — see `keepRedactedMatches`. A
 * lexicon match IS its redaction here (`buildRedactionPlan` burns in straight
 * from `detections`, so no separate record could carry it), which means
 * removing one un-redacts the document. A candidate nobody touched is the
 * lexicon's suggestion and leaves with the lexicon; a match the reviewer
 * redacted is their work and their disclosure record, and it stays — with the
 * card, so it can still say which lexicon and which entry found it after both
 * are gone.
 *
 * SAFE BECAUSE IT IS UNDOABLE, which is why it has no confirmation in front of
 * it. The reducer pushes an undo snapshot, and `snapshotFromSession` captures
 * `detections` and `appliedLexicons` together — so Ctrl+Z restores the cards,
 * the matches and the redaction decisions on them in one step. That is the
 * editor's idiom for a destructive edit (deleting a redaction works the same
 * way); a modal here would be a second mechanism for a problem the first one
 * already solves.
 *
 * It also drops the id from `sessionAppliedLexiconIds`. Leaving it there would
 * let the next burst of page text re-apply the lexicon the reviewer just
 * removed (BR-48's re-evaluation), which reads as the removal silently failing.
 *
 * NOT a dismissal of its matches. `DELETE_DETECTION_MATCH` tombstones a match
 * so re-application cannot resurrect it (BR-41); this leaves no tombstones, so
 * applying the lexicon again brings the candidates back in full and folds them
 * onto the redactions that stayed (M2). Removing and re-applying is the
 * recovery path when undo is no longer available.
 */
export const REMOVE_LEXICON_FROM_DOCUMENT = createAction<{
  lexiconId: string;
}>('redact/documents/REMOVE_LEXICON_FROM_DOCUMENT');

/**
 * Record the upload dialog's lexicon selection against a freshly created
 * document (VE-24948 / U5, `U5-FR14`).
 *
 * An ACTION rather than a direct `call` from the uploads saga. That call made
 * `uploads` a second writer of the `document-redaction-data` asset the
 * documents module owns — including its 5-slot history rotation — while every
 * other cross-duck interaction here couples via actions (review-gate M11).
 * Evaluates nothing: matching needs page text, which does not exist until the
 * editor opens the document (BR-49).
 */
export const RECORD_UPLOAD_LEXICON_SELECTION = createAction<{
  tdoId: string;
  /** Upload URL, which keys this file's share of the staged batch. */
  fileUrl: string;
}>('redact/documents/RECORD_UPLOAD_LEXICON_SELECTION');

/**
 * Show or hide one lexicon's rows in the detections list (VE-24948 / U5).
 *
 * A sibling of `SET_DETECTION_FILTER`: lexicon rows have no `piiType`, so they
 * are filtered by `lexiconId` instead. In redux rather than component state so
 * the choice survives the panel's routine remount (review-gate M5).
 */
export const SET_LEXICON_FILTER = createAction<{
  lexiconId: string;
  visible: boolean;
}>('redact/documents/SET_LEXICON_FILTER');

/** Show or hide EVERY lexicon at once — the dropdown's All checkbox. */
export const SET_ALL_LEXICON_FILTERS = createAction<{
  visible: boolean;
  /** Ids currently offered, so hiding all can enumerate them. */
  lexiconIds: string[];
}>('redact/documents/SET_ALL_LEXICON_FILTERS');

/** The Detections tab's `All | PII | Lexicon` strip (VE-30011). */
export const SET_DETECTION_SOURCE = createAction<{
  source: DetectionSource;
}>('redact/documents/SET_DETECTION_SOURCE');

export const SET_DETECTION_FILTER = createAction<{
  category: 'direct' | 'indirect';
  piiType: PiiTypeId;
  enabled: boolean;
}>('redact/documents/SET_DETECTION_FILTER');
/**
 * The All / Clear action on the PII band, as ONE action rather than N
 * `SET_DETECTION_FILTER`s.
 *
 * Both lists are named explicitly instead of being derived in the reducer
 * from the taxonomy constants: the caller knows which types are actually on
 * screen, and only those should move. A type the engine has not reported is
 * left alone, so if a later run finds it, it arrives visible.
 */
export const SET_ALL_DETECTION_FILTERS = createAction<{
  enabled: boolean;
  direct: PiiTypeId[];
  indirect: PiiTypeId[];
}>('redact/documents/SET_ALL_DETECTION_FILTERS');

export const SET_DETECTION_SEARCH = createAction<{ phrase: string }>(
  'redact/documents/SET_DETECTION_SEARCH'
);
export const SET_DETECTION_GEOMETRY_OVERRIDE = createAction<{
  key: DetectionRectKey;
  geometry: BoundingBox;
}>('redact/documents/SET_DETECTION_GEOMETRY_OVERRIDE');
// ---------------------------------------------------------------------------
// Manual redactions
// ---------------------------------------------------------------------------

export const CREATE_MANUAL_REDACTION = createAction<{ redaction: Redaction }>(
  'redact/documents/CREATE_MANUAL_REDACTION'
);
/**
 * Bulk variant of CREATE_MANUAL_REDACTION — adds every redaction in one
 * shot under a single undo snapshot, so a "Redact all keyword matches"
 * action is one undo step instead of N.
 */
export const CREATE_MANUAL_REDACTIONS = createAction<{
  redactions: Redaction[];
}>('redact/documents/CREATE_MANUAL_REDACTIONS');
/**
 * Region text-selection redact. Like CREATE_MANUAL_REDACTIONS but, for each
 * incoming `text-select` box, merges into an existing active `text-select`
 * redaction it overlaps on the same page (extending that box to the union)
 * instead of stacking a duplicate. Keyword / polygon / page-redact / manual
 * boxes are never merged into. The resulting boxes (merged + newly created)
 * become the selection, all under one undo snapshot.
 */
export const CREATE_OR_MERGE_TEXT_REDACTIONS = createAction<{
  redactions: Redaction[];
}>('redact/documents/CREATE_OR_MERGE_TEXT_REDACTIONS');
export const MOVE_REDACTION = createAction<{
  id: string;
  geometry: BoundingBox;
}>('redact/documents/MOVE_REDACTION');
export const RESIZE_REDACTION = createAction<{
  id: string;
  geometry: BoundingBox;
}>('redact/documents/RESIZE_REDACTION');
/** Replace a polygon redaction's vertices (vertex-drag editing). */
export const UPDATE_REDACTION_POINTS = createAction<{
  id: string;
  points: Array<{ x: number; y: number }>;
}>('redact/documents/UPDATE_REDACTION_POINTS');
/**
 * Update the display name of a redaction (VE-20839). Region cards
 * use this for inline title edits; an empty / whitespace-only `name`
 * is treated as "remove custom name" and the card falls back to its
 * default `Overlay N`.
 */
export const SET_REDACTION_NAME = createAction<{
  id: string;
  name: string;
}>('redact/documents/SET_REDACTION_NAME');
export const DELETE_REDACTIONS_BY_PHRASE = createAction<{ phrase: string }>(
  'redact/documents/DELETE_REDACTIONS_BY_PHRASE'
);
/**
 * Toggle whether a redaction is drawn on every page (VE-20838 "Stick
 * to all pages"). When true, the same geometry renders on every page
 * and the burn-in worker expands the entry per-page at export.
 */
export const TOGGLE_REDACTION_ALL_PAGES = createAction<{ id: string }>(
  'redact/documents/TOGGLE_REDACTION_ALL_PAGES'
);
export const DELETE_REDACTION = createAction<{ id: string }>(
  'redact/documents/DELETE_REDACTION'
);
/**
 * Bulk delete (VE-21982 follow-up). Filters out every redaction whose id is
 * in `ids` in a single reducer pass + one undo snapshot, so a batch delete
 * shows up as one undo step regardless of how many redactions were removed.
 */
export const DELETE_REDACTIONS = createAction<{ ids: string[] }>(
  'redact/documents/DELETE_REDACTIONS'
);
/**
 * Flip the `redacted` flag on a set of redactions (VE-20839). Un-redacted
 * records stay in state but render/burn-in code paths skip them, so the user
 * can toggle a phrase group on/off without losing the work.
 */
export const SET_REDACTIONS_REDACTED = createAction<{
  ids: string[];
  redacted: boolean;
}>('redact/documents/SET_REDACTIONS_REDACTED');
/**
 * Promote a set of redactions to `origin: 'keyword'`. Used by the
 * text-selection "Redact All Matches" flow: a phrase the user first redacted
 * as a one-off text selection becomes part of a keyword group once they redact
 * every occurrence, so the original box joins the same per-phrase card instead
 * of lingering as a separate "Text" card (and the card's "N of M redacted"
 * count covers every match). No-op for ids already on the keyword origin.
 */
export const CONVERT_REDACTIONS_TO_KEYWORD = createAction<{
  ids: string[];
}>('redact/documents/CONVERT_REDACTIONS_TO_KEYWORD');
/**
 * Select payload supports three modes so the same action covers single
 * click (replace), shift/cmd+click (toggle), and a future marquee tool
 * (add). `id: null` with mode 'replace' clears the selection.
 *
 * - `replace` (default): selection becomes `[id]`, or `[]` if id is null.
 * - `toggle`: flip the id's membership in the current selection. No-op
 *   when id is null.
 * - `add`: union the id into the current selection (used by marquee).
 *   No-op when id is null.
 */
export const SELECT_REDACTION = createAction<{
  id: string | null;
  mode?: 'replace' | 'toggle' | 'add';
}>('redact/documents/SELECT_REDACTION');

/**
 * Bulk selection — used by the marquee + lasso multi-select tools. Same
 * mode semantics as SELECT_REDACTION:
 * - `replace` (default): selection becomes the passed ids.
 * - `add`: union the ids into the current selection.
 * - `toggle`: flip each id's membership (XOR).
 *
 * An empty array with `replace` clears the selection, mirroring
 * SELECT_REDACTION({ id: null }).
 */
export const SELECT_REDACTIONS = createAction<{
  ids: string[];
  mode?: 'replace' | 'toggle' | 'add';
}>('redact/documents/SELECT_REDACTIONS');

/**
 * PII counterparts of SELECT_REDACTION(S). Keys are
 * `${detectionId}|${page}` strings — one selection unit per overlay
 * rect on the canvas. Same `mode` semantics as the redaction
 * actions.
 */
export const SELECT_DETECTION_ROW = createAction<{
  key: string | null;
  mode?: 'replace' | 'toggle' | 'add';
}>('redact/documents/SELECT_DETECTION_ROW');
export const SELECT_DETECTION_ROWS = createAction<{
  keys: string[];
  mode?: 'replace' | 'toggle' | 'add';
}>('redact/documents/SELECT_DETECTION_ROWS');

// ---------------------------------------------------------------------------
// Codes
// ---------------------------------------------------------------------------

export const ASSIGN_CODE = createAction<{
  targetType: 'redaction' | 'detection';
  targetId: string;
  mode?: 'single' | 'all-instances';
  code: RedactionCodeAssignment | null;
}>('redact/documents/ASSIGN_CODE');

export const CLEAR_REDACTIONS = createAction(
  'redact/documents/CLEAR_REDACTIONS'
);

// ---------------------------------------------------------------------------
// Page redact
// ---------------------------------------------------------------------------

export const TOGGLE_PAGE_EXCLUDE = createAction<{ page: number }>(
  'redact/documents/TOGGLE_PAGE_EXCLUDE'
);
export const TOGGLE_PAGE_REDACT = createAction<{ page: number }>(
  'redact/documents/TOGGLE_PAGE_REDACT'
);

// ---------------------------------------------------------------------------
// Keyword search
// ---------------------------------------------------------------------------

export const KEYWORD_SEARCH = createAction<{ phrase: string }>(
  'redact/documents/KEYWORD_SEARCH'
);
/**
 * Toggle the match-case / whole-word options. The reducer stores the
 * new value AND re-runs the current search by delegating back through
 * the saga — flipping a toggle should immediately re-evaluate matches
 * without the user having to retype the phrase.
 */
export const SET_KEYWORD_SEARCH_OPTIONS = createAction<{
  matchCase?: boolean;
  wholeWord?: boolean;
}>('redact/documents/SET_KEYWORD_SEARCH_OPTIONS');
export const KEYWORD_MATCHES_READY = createAction<{
  phrase: string;
  matches: import('./models').KeywordMatch[];
}>('redact/documents/KEYWORD_MATCHES_READY');

// ---------------------------------------------------------------------------
// Undo / Redo
// ---------------------------------------------------------------------------

export const UNDO = createAction('redact/documents/UNDO');
export const REDO = createAction('redact/documents/REDO');

// ---------------------------------------------------------------------------
// Save / Lock
// ---------------------------------------------------------------------------

/**
 * Optional `requestId` lets callers (e.g. the export saga) correlate a
 * specific SAVE_NOW with its eventual SAVE_SUCCESS / SAVE_FAILURE. Without
 * it, racing on the next SUCCESS/FAILURE could be satisfied by an
 * unrelated in-flight auto-save. The auto-save loop's own ticks pass
 * undefined; only explicitly-correlated callers set the id.
 */
/**
 * VE-26422: `expectedRedactedVersion` is set only by the save the export forces,
 * and names the output version that burn-in is about to produce. It is what
 * marks the resulting asset as sent for burn-in; an ordinary save omits it.
 */
export const SAVE_NOW = createAction<
  { requestId?: string; expectedRedactedVersion?: number } | undefined
>('redact/documents/SAVE_NOW');
/**
 * VE-28061: every save names the document it persists.
 *
 * The export's pre-burn-in save outlives the editor, so by the time one of
 * these fires the open session — if any — may be a DIFFERENT document. The
 * reducer and the `SAVE_SUCCESS` side-effects apply them only when the ids
 * match. Without the id a detached save marked whatever document was open
 * `clean` (stranding its unsaved edits behind a disabled Save button and an
 * auto-save loop that skips clean sessions) and stamped it with the exporting
 * document's burn-in version label, corrupting its staleness verdict.
 */
export const SAVE_REQUEST = createAction<{
  tdoId: TDOId;
  requestId?: string;
}>('redact/documents/SAVE_REQUEST');
export const SAVE_SUCCESS = createAction<{
  tdoId: TDOId;
  savedAt: string;
  requestId?: string;
  /** `save.rev` of the snapshot persisted, so the reducer can detect a mid-save edit. */
  savedRev: number;
  /** VE-26422: the version this save was sent to produce, when it was. */
  expectedRedactedVersion?: number;
}>('redact/documents/SAVE_SUCCESS');
export const SAVE_FAILURE = createAction<{
  tdoId: TDOId;
  code: string;
  message: string;
  requestId?: string;
}>('redact/documents/SAVE_FAILURE');

/**
 * Load the pages a burned-in output actually dropped, from the
 * `document-redaction-data` asset that burn-in consumed.
 *
 * Dispatched when Side-by-Side View opens: its column mapping must follow the
 * redacted FILE, not `session.excludedPages` (the live editor state), which
 * drifts the moment a page is deleted or restored after the last burn-in.
 * Cached per `versionId`, so re-opening the view doesn't refetch.
 */
export const LOAD_REDACTED_VERSION_PAGES = createAction<{
  versionId: string;
}>('redact/documents/LOAD_REDACTED_VERSION_PAGES');
export const LOAD_REDACTED_VERSION_PAGES_SUCCESS = createAction<{
  versionId: string;
  excludedPages: number[];
}>('redact/documents/LOAD_REDACTED_VERSION_PAGES_SUCCESS');
/**
 * Non-fatal: consumers fall back to `session.excludedPages`, which is correct
 * whenever the editor hasn't diverged from the output. Carries the reason for
 * logging only — nothing is surfaced to the user, since Side-by-Side already
 * warns when the redacted file is stale.
 */
export const LOAD_REDACTED_VERSION_PAGES_FAILURE = createAction<{
  versionId: string;
  message: string;
}>('redact/documents/LOAD_REDACTED_VERSION_PAGES_FAILURE');

// ---------------------------------------------------------------------------
// Redaction status (VE-21914)
//
// Persists the per-TDO status tag the rest of the app reads from
// `details.tags[IN_REDACTION_TAG_KEY]`. Optimistic: the reducer applies the
// new value immediately, the saga writes through, and FAILURE rolls back to
// `previousStatus`.
// ---------------------------------------------------------------------------

export const SET_REDACTION_STATUS_REQUEST = createAction<{
  /**
   * Canonical status identity (`redactionStatusKey ?? redactionStatus`) — the
   * value the rest of the app matches / locks / colors on. Built-in label or
   * custom `statusKey` UUID.
   */
  status: string;
  /** Human-readable name persisted as the tag's `redactionStatus` (VE-24546). */
  statusLabel: string;
  /** Stable UUID persisted as `redactionStatusKey`; custom statuses only. */
  statusKey?: string;
  /**
   * Pre-change value the reducer rolls back to on FAILURE. The dropdown
   * captures this from `session.redactionStatus` at click time, before
   * dispatching — so it always reflects the last committed value rather
   * than a possibly-stale optimistic one from a prior in-flight request.
   */
  previousStatus: string | null;
  /** Frozen name to roll `session.redactionStatusName` back to on FAILURE. */
  previousStatusName: string | null;
  /**
   * Correction note for the status history (VE-26730). Captured by the
   * CorrectionNoteModal on transitions to Correction Needed; read by the
   * statusHistory watcher, ignored by this module's own saga/reducer.
   */
  note?: string;
  /**
   * Optional reply to the note the file was carrying, captured when this
   * transition moves it OFF Correction Needed (VE-26730). Same transport as
   * `note`: read by the statusHistory watcher, ignored here.
   */
  response?: string;
  responseType?: StatusHistoryNoteOutcome;
  /**
   * True for programmatic transitions (the To Do → Draft auto-promote) so the
   * status history records them with isSystem: true (VE-26730).
   */
  isSystemTransition?: boolean;
}>('redact/documents/SET_REDACTION_STATUS_REQUEST');
export const SET_REDACTION_STATUS_SUCCESS = createAction<{
  status: string;
  statusLabel: string;
  statusKey?: string;
  /**
   * The TDO the mutation persisted against. Lets the statusHistory watcher
   * correlate this terminal action with its own request instead of matching on
   * type alone (VE-26730) — a type-only match records the wrong file's
   * transition if a second producer or session ever appears.
   */
  tdoId: string;
}>('redact/documents/SET_REDACTION_STATUS_SUCCESS');
export const SET_REDACTION_STATUS_FAILURE = createAction<{
  previousStatus: string | null;
  previousStatusName: string | null;
  message: string;
  /** See SET_REDACTION_STATUS_SUCCESS.tdoId — same correlation role. */
  tdoId: string;
}>('redact/documents/SET_REDACTION_STATUS_FAILURE');

// ---------------------------------------------------------------------------
// Document rename (VE-21914 follow-on)
//
// Updates the TDO's user-visible `name` (the filename shown in the editor
// header). Mirrors the SET_REDACTION_STATUS triplet: REQUEST applies
// optimistically, the saga writes through, FAILURE rolls back to
// `previousName`. The video editor's `setTDOName` flow is in `mediaDetails`
// which the doc editor doesn't load, so we own a parallel write path.
// ---------------------------------------------------------------------------

export const RENAME_DOCUMENT_REQUEST = createAction<{
  name: string;
  /** Pre-change name the reducer rolls back to on FAILURE. */
  previousName: string;
}>('redact/documents/RENAME_DOCUMENT_REQUEST');
export const RENAME_DOCUMENT_SUCCESS = createAction<{ name: string }>(
  'redact/documents/RENAME_DOCUMENT_SUCCESS'
);
export const RENAME_DOCUMENT_FAILURE = createAction<{
  previousName: string;
  message: string;
}>('redact/documents/RENAME_DOCUMENT_FAILURE');

// ---------------------------------------------------------------------------
// Export / Push
// ---------------------------------------------------------------------------

export const EXPORT_REQUEST = createAction('redact/documents/EXPORT_REQUEST');
// VE-28061: the export lifecycle actions carry the TDO they belong to. A
// burn-in keeps running after the editor is closed, so by the time one of these
// fires the open session — if any — may be another document's; the reducer
// applies them only when the ids match.
export const EXPORT_SUBMITTED = createAction<{ tdoId: TDOId; jobId: string }>(
  'redact/documents/EXPORT_SUBMITTED'
);
export const EXPORT_COMPLETED = createAction<{
  tdoId: TDOId;
  /** Asset id of the new redacted-document; doubles as the version id. */
  versionId: string;
  /** Monotonic version number from the new asset's details.version. */
  version: number;
  /** signedUri for previewing the redacted PDF. May be null until refreshed. */
  redactedAssetUrl: string | null;
  /** User-visible filename of the redacted asset (e.g. "<name>[REDACTED].pdf"). */
  name: string | null;
  /** Byte size of the burned-in PDF. */
  assetSize: number;
  /** Page count of the burned-in PDF. */
  numPages: number;
  /** Number of redactions that were burned in (zero allowed). */
  redactionCount: number;
  /** Per-category redaction metrics (mirror `details.metrics.*`). */
  textSelect: TextSelectMetrics;
  keywords: KeywordMetrics;
  pii: PiiMetrics;
  /** Count of pages dropped from the output (VE-20838). */
  pagesRemoved: number;
  /** Count of pages marked for full-page redaction in the output. */
  pagesFullyRedacted: number;
  /**
   * signedUri of the `redact-audit-log` CSV asset for this version (VE-20840),
   * or null if audit-log asset creation failed (non-fatal). Drives the
   * "Download Audit Log" button on the Redacted File tab.
   */
  auditLogUrl: string | null;
  /**
   * The new redacted-document asset's SERVER `createdDateTime` (VE-30011).
   * casemedia records the output on the file's row from this action, and the
   * staleness check compares it with server-stamped redaction-data times — a
   * browser clock would read a fresh file as stale under skew.
   */
  createdDateTime: string;
  /**
   * The audit-log CSV asset created for this version, or null when that
   * (non-fatal) step failed. casemedia patches it beside the output, because
   * the burn-in has already deleted the previous one.
   */
  auditLogAsset: { id: string; name: string; signedUri: string } | null;
}>('redact/documents/EXPORT_COMPLETED');

/**
 * Fired when the user downloads the redacted file. Reducer optimistically
 * bumps `latest.downloads`; saga persists the new count to the asset via
 * GraphQL updateAsset. Mirrors video's INCREMENT_DOWNLOAD_COUNT.
 */
export const INCREMENT_REDACTED_DOCUMENT_DOWNLOAD = createAction<{
  versionId: string;
}>('redact/documents/INCREMENT_REDACTED_DOCUMENT_DOWNLOAD');
export const EXPORT_FAILED = createAction<{ tdoId: TDOId; message: string }>(
  'redact/documents/EXPORT_FAILED'
);

/**
 * VE-20837: delete a single `redacted-document` asset from the TDO.
 * Removes the corresponding entry from `session.versionHistory` on success.
 * Today the ticket only permits one redacted-document asset at a time, but
 * the action takes an explicit `versionId` so it's safe if the retention
 * policy ever loosens to allow multiple versions.
 */
export const DELETE_REDACTED_DOCUMENT_REQUEST = createAction<{
  versionId: string;
}>('redact/documents/DELETE_REDACTED_DOCUMENT_REQUEST');
export const DELETE_REDACTED_DOCUMENT_SUCCESS = createAction<{
  versionId: string;
  /**
   * The file the asset belonged to. Carried so casemedia can take the output
   * off that file's rows (VE-30011) — the inverse of the `EXPORT_COMPLETED`
   * patch, and what turns the Documents panel marker back to "not redacted".
   * The reducer here does not need it; it only ever touches the open session.
   */
  tdoId: TDOId;
}>('redact/documents/DELETE_REDACTED_DOCUMENT_SUCCESS');
export const DELETE_REDACTED_DOCUMENT_FAILURE = createAction<{
  versionId: string;
  message: string;
}>('redact/documents/DELETE_REDACTED_DOCUMENT_FAILURE');

/**
 * VE-22300: download a "redaction report" CSV for the last redacted file. The
 * saga loads the `document-redaction-data` asset tied to that file, formats it
 * into the report, and triggers a browser download. No payload — the saga
 * resolves the latest version from `session.versionHistory`.
 */
export const DOWNLOAD_REDACTION_REPORT_REQUEST = createAction(
  'redact/documents/DOWNLOAD_REDACTION_REPORT_REQUEST'
);
export const DOWNLOAD_REDACTION_REPORT_FAILURE = createAction<{
  message: string;
}>('redact/documents/DOWNLOAD_REDACTION_REPORT_FAILURE');

// ---------------------------------------------------------------------------
// "Send to" external integrations (VE-23442)
//
// One action per integration; each sends the latest redacted PDF (+ audit log)
// to the configured endpoint via the shared `src/helpers/*API.ts` helpers.
// The optional auth payload is only used by GovQA (username/password dialog).
// ---------------------------------------------------------------------------

export const SEND_TO_GOVQA = createAction<IntegrationAuth | undefined>(
  'redact/documents/SEND_TO_GOVQA'
);
export const SEND_TO_FOIAXPRESS = createAction<IntegrationAuth | undefined>(
  'redact/documents/SEND_TO_FOIAXPRESS'
);
export const SEND_TO_CASEPOINT = createAction<IntegrationAuth | undefined>(
  'redact/documents/SEND_TO_CASEPOINT'
);
export const SEND_TO_EXTERRO = createAction<IntegrationAuth | undefined>(
  'redact/documents/SEND_TO_EXTERRO'
);
export const SEND_TO_NUIX = createAction<IntegrationAuth | undefined>(
  'redact/documents/SEND_TO_NUIX'
);
export const SEND_TO_EXTERNAL_INTEGRATION = createAction<
  IntegrationAuth | undefined
>('redact/documents/SEND_TO_EXTERNAL_INTEGRATION');

/**
 * Fetch the org's `redact-export` integration config so the generic "Send to"
 * button can render its custom label. Dispatched by RedactedFilePreview on
 * mount; the saga fetches and patches `integrations.externalIntegration.config`.
 */
export const FETCH_EXTERNAL_INTEGRATION_CONFIG = createAction(
  'redact/documents/FETCH_EXTERNAL_INTEGRATION_CONFIG'
);

/**
 * Merge a partial update into one integration slice (spinner flags, dialog
 * visibility, messages, GovQA sessionId, fetched config). Mirrors mediaDetails'
 * `patch<Vendor>State` actions, collapsed into one keyed action.
 */
export const PATCH_INTEGRATION_STATE = createAction<{
  key: IntegrationKey;
  patch: Partial<IntegrationState>;
}>('redact/documents/PATCH_INTEGRATION_STATE');

// ---------------------------------------------------------------------------
// PII detection re-run (VE-21915)
// ---------------------------------------------------------------------------

/**
 * Triggered by the "Run PII Detection" button in the PII subtab for documents
 * that were ingested without PII. The saga creates a job pointed directly at
 * the document PII detection engine, using the session's PDF signed URL.
 * Reducer optimistically flips `piiEngineState` to `processing`.
 */
/**
 * Run the Redact Document OCR engine on the document already open, from the
 * per-page "no searchable text" notice.
 *
 * The job's own id comes back on SUCCESS, which is what hands it to the shared
 * engine-status poller — nothing polls from the saga that starts it.
 */
export const RUN_OCR_DETECTION = createAction(
  'redact/documents/RUN_OCR_DETECTION'
);
export const RUN_OCR_DETECTION_SUCCESS = createAction<{
  /**
   * WHICH DOCUMENT THIS RUN BELONGS TO. The workspace keeps several documents
   * open and switching between them is one click, so a run started on A must
   * not be picked up by the poller now watching B — it would fabricate a
   * failure there, or leave it waiting out the full task budget for a task B
   * will never have. Same reasoning as the `EXPORT_*` actions under VE-28061.
   */
  tdoId: TDOId;
  jobId: string;
  /**
   * The OCR task the job was created WITH, straight from the create response.
   *
   * The poller seeds its discovery with this instead of going looking. Without
   * it, a re-run left the watch with an active engine that had neither a result
   * it may trust nor a task id — the one state that forces full re-discovery —
   * so it paged the TDO's entire task list every tick until the new task became
   * queryable. That is the exact window "discover once, then poll by id" exists
   * to avoid paying in.
   *
   * Optional: an API that returns no task records leaves this undefined and the
   * poller falls back to finding it. Undefined means "not told", never "no
   * task".
   */
  ocrTaskId?: string;
}>('redact/documents/RUN_OCR_DETECTION_SUCCESS');
export const RUN_OCR_DETECTION_FAILURE = createAction<{
  /**
   * Scoped like the SUCCESS above, and for a sharper reason: this reverts the
   * optimistic `processing` the click set, and it is dispatched from a catch
   * block AFTER a round trip — by which time the reviewer may well be looking
   * at another document. Unscoped, a failed submission on A cleared the
   * spinner on B.
   */
  tdoId: TDOId;
  message: string;
}>('redact/documents/RUN_OCR_DETECTION_FAILURE');

export const RUN_PII_DETECTION = createAction(
  'redact/documents/RUN_PII_DETECTION'
);
export const RUN_PII_DETECTION_SUCCESS = createAction<{
  /** Which document this run belongs to — see `RUN_OCR_DETECTION_SUCCESS`. */
  tdoId: TDOId;
  jobId: string;
  /** The PII task the job was created with — see `RUN_OCR_DETECTION_SUCCESS`. */
  piiTaskId?: string;
  /** The OCR task, when this job chains one ahead of detection. */
  ocrTaskId?: string;
  /**
   * This job runs OCR ahead of detection, so the engine-status poller has to
   * revive the OCR watch as well as PII's. Without it OCR is left announced as
   * `processing` with nothing watching the task, and the notice spins for ever
   * while PII — which the poller IS watching — reports its results.
   */
  withOcr?: boolean;
}>('redact/documents/RUN_PII_DETECTION_SUCCESS');
export const RUN_PII_DETECTION_FAILURE = createAction<{
  /** Scoped like `RUN_OCR_DETECTION_FAILURE`, and for the same reason. */
  tdoId: TDOId;
  message: string;
}>('redact/documents/RUN_PII_DETECTION_FAILURE');

/**
 * EVERY ENGINE OUTCOME BELOW NAMES ITS DOCUMENT.
 *
 * These are all observations about one TDO's engines, produced asynchronously
 * and arriving whenever the API answers. The workspace keeps several documents
 * open and switching is one click, so "the session that happens to be on
 * screen" is not the session an outcome is about — and writing to it anyway
 * was silently wrong in the way that matters most: `PII_RESULTS_LOADED` would
 * fold ANOTHER document's detections into the open one, where they read as
 * findings about the page in front of the reviewer.
 *
 * The reducers resolve `tdoId` through `sessionFor`, so an outcome also reaches
 * a document parked in `suspendedSessions` and is dropped only when that
 * document is genuinely gone. `OCR_MANIFEST_LOADED` carried a `tdoId` from the
 * start for exactly this reason; the rest have caught up.
 */

/**
 * Engine state observed by the on-open lifecycle check / poll loop:
 * a PII detection task is in flight on the TDO. Reducer sets
 * `piiEngineState = 'processing'` (without the user having clicked Run).
 */
export const PII_DETECTION_PROCESSING = createAction<{ tdoId: TDOId }>(
  'redact/documents/PII_DETECTION_PROCESSING'
);

/**
 * The on-open PII check settled with no job running and no existing results —
 * i.e. PII genuinely hasn't been run. Reducer sets `piiEngineState = 'not-run'`
 * so the subtab leaves the loading spinner and shows "Run PII Detection". Also
 * used to resolve the `checking` state when the check errors or the engine
 * isn't configured, so the spinner never sticks.
 */
export const PII_NOT_RUN = createAction<{ tdoId: TDOId }>(
  'redact/documents/PII_NOT_RUN'
);

/**
 * PII results were found + parsed from the engine's `vtn-standard` asset.
 * Reducer replaces `session.detections` and sets `piiEngineState = 'done'`.
 */
export const PII_RESULTS_LOADED = createAction<{
  tdoId: TDOId;
  detections: PiiDetection[];
}>('redact/documents/PII_RESULTS_LOADED');

// ---------------------------------------------------------------------------
// Redact Document OCR
// ---------------------------------------------------------------------------

/**
 * An OCR task is running for the document now open. Under open-early this is
 * the ORDINARY case, not an edge one: nothing gates the open on OCR, so a
 * document uploaded seconds ago opens straight into this state.
 * Reducer sets `ocrEngineState = 'processing'`.
 */
export const OCR_PROCESSING = createAction<{ tdoId: TDOId }>(
  'redact/documents/OCR_PROCESSING'
);

/**
 * No OCR task exists on this TDO. Every document ingested before the OCR
 * engine existed lands here, as does a document whose OCR engine isn't
 * configured, and so does any
 * error in the check — the state must always resolve so nothing hangs on
 * `checking`. Reducer sets `ocrEngineState = 'not-run'`.
 *
 * This is NOT "OCR found nothing": it means nothing is known about OCR for this
 * document, so no OCR messaging is shown at all.
 */
export const OCR_NOT_RUN = createAction<{ tdoId: TDOId }>(
  'redact/documents/OCR_NOT_RUN'
);

/**
 * The OCR task reached a terminal state other than `complete`. The document
 * still opens — search is degraded, not the document missing — and the reviewer
 * is told rather than being left to assume the pages were read.
 */
export const OCR_FAILED = createAction<{ tdoId: TDOId; reason?: string }>(
  'redact/documents/OCR_FAILED'
);

/**
 * Show / hide the overlay marking the areas OCR could not read. Toggled from the
 * document-level best-effort notice.
 */
export const TOGGLE_OCR_REGIONS = createAction<{ show: boolean }>(
  'redact/documents/TOGGLE_OCR_REGIONS'
);

/**
 * The OCR status could not be established — the check errored, or a job stayed
 * in flight far longer than it takes to create its tasks.
 *
 * Distinct from `OCR_NOT_RUN` on purpose. "OCR never ran" licenses the flat
 * "no pages have searchable text — manual review recommended", and that is a
 * claim about search coverage a reviewer will act on. Inferring it from a failed
 * lookup states it of a document that may well be mid-OCR. Reducer sets
 * `ocrEngineState = 'indeterminate'`, which shows the reviewer nothing about OCR
 * rather than something false.
 */
export const OCR_INDETERMINATE = createAction<{ tdoId: TDOId }>(
  'redact/documents/OCR_INDETERMINATE'
);

/**
 * The OCR task this session opened alongside has finished, so a searchable copy
 * of the document is now the TDO's primary asset. The open session keeps
 * rendering the file it pinned until the user takes the offer — nothing swaps
 * the PDF under work in progress on its own. Reducer sets
 * `ocrEngineState = 'refresh-available'`.
 */
/** OCR's task was created 12h ago and has still not finished. */
export const OCR_TIMED_OUT = createAction<{ tdoId: TDOId }>(
  'redact/documents/OCR_TIMED_OUT'
);

/** PII's task ended in a terminal state other than `complete`. */
export const PII_DETECTION_FAILED = createAction<{ tdoId: TDOId }>(
  'redact/documents/PII_DETECTION_FAILED'
);

/** PII's task was created 12h ago and has still not finished. */
export const PII_TIMED_OUT = createAction<{ tdoId: TDOId }>(
  'redact/documents/PII_TIMED_OUT'
);

export const OCR_REFRESH_AVAILABLE = createAction<{ tdoId: TDOId }>(
  'redact/documents/OCR_REFRESH_AVAILABLE'
);

/**
 * The user accepted the reload offered by `OCR_REFRESH_AVAILABLE`. The saga
 * flushes unsaved work and re-opens the document against the new primary asset.
 * Redaction boxes survive because the searchable copy keeps the original's page
 * geometry — the engine adds a text layer and touches no page box.
 */
export const RELOAD_FOR_OCR = createAction<{ page?: number } | undefined>(
  'redact/documents/RELOAD_FOR_OCR'
);

/**
 * The reload has finished having an effect on lexicons — one way or another.
 *
 * `RELOAD_FOR_OCR` raises `ocrReloadInProgress` so the panel can say matches
 * are being updated from the moment the reviewer asks, rather than from the
 * much later moment the re-run can actually start. Something has to lower it
 * again, and the honest answer is not knowable from any one action:
 *
 *   - the re-run reported — `LEXICON_APPLY_SUCCESS` / `_FAILURE` cover that;
 *   - the reload never happened, because the pre-reload save failed and the
 *     saga returned without re-opening anything;
 *   - the reload happened and there was nothing stale to re-run, so no run is
 *     ever coming to report.
 *
 * The last two are what this action is for, dispatched from the two places that
 * can actually tell: `handleReloadForOcr`'s failure exit, and the lexicon
 * re-evaluation watcher when it finds nothing pending. Both were previously
 * inferred in the reducer — the failure exit not at all (the notice stuck for
 * the session), and the nothing-stale case by reading `state.session` inside
 * `OPEN_DOCUMENT_SUCCESS`, which is the OUTGOING document at that point.
 */
export const OCR_RELOAD_SETTLED = createAction(
  'redact/documents/OCR_RELOAD_SETTLED'
);

/**
 * The OCR manifest was found, downloaded and accepted. Reducer stores it and
 * sets `ocrEngineState = 'done'`. `tdoId` guards it against landing on a session
 * that has since moved to another document.
 */
export const OCR_MANIFEST_LOADED = createAction<{
  tdoId: TDOId;
  manifest: OcrManifest;
}>('redact/documents/OCR_MANIFEST_LOADED');

// ---------------------------------------------------------------------------
// Audit (forwarded to mediaDetails sendEvent path)
// ---------------------------------------------------------------------------

export const LOG_AUDIT_EVENT = createAction<{
  action: string;
  payload?: Record<string, unknown>;
  /**
   * Which document the event is about, for the emitters that cannot assume it
   * is the one on screen.
   *
   * Almost every watcher in `auditLogs.ts` runs on a user action against the
   * open document, so the emitter defaults to the live session. The burn-in
   * does not: it outlives a document switch by design (VE-28061), so by the
   * time it records which file it cut from, the editor may be showing another
   * document — or none. Without this, that event was filed against the wrong
   * TDO, which for an audit trail is worse than not recording it.
   */
  tdoId?: TDOId;
}>('redact/documents/LOG_AUDIT_EVENT');

// ---------------------------------------------------------------------------
// Canvas / text extraction
// ---------------------------------------------------------------------------

export const SET_PAGE_TEXT = createAction<{ page: number; text: string }>(
  'redact/documents/SET_PAGE_TEXT'
);

/**
 * Per-page searchability captured at render time (VE-23390). 'searchable'
 * has extractable text; 'unsearchable' is a no-text page that still paints
 * real content (scan, rasterized/flattened, or text converted to vector
 * outlines) so native keyword search can't reach it; 'blank' is a genuinely
 * empty page. The search bar aggregates this to warn when some/all pages
 * can't be searched.
 */
export const SET_PAGE_SEARCHABILITY = createAction<{
  page: number;
  state: 'searchable' | 'unsearchable' | 'blank';
}>('redact/documents/SET_PAGE_SEARCHABILITY');

/**
 * Collapse / re-expand the per-page "no searchable text" notice (VE-23390).
 * Document-wide so dismissing it on one page hides it on every page where it
 * appears (and re-expanding restores it everywhere).
 */
export const SET_OCR_WARNING_DISMISSED = createAction<boolean>(
  'redact/documents/SET_OCR_WARNING_DISMISSED'
);

/**
 * Collapse / re-expand the per-page "processed by OCR" notice. Document-wide,
 * and separate from the warning above on purpose — see `ocrProcessedDismissed`.
 */
export const SET_OCR_PROCESSED_DISMISSED = createAction<boolean>(
  'redact/documents/SET_OCR_PROCESSED_DISMISSED'
);

/**
 * Stash per-page text-item geometry captured from pdfjs at page-render
 * time so the keyword search saga can resolve character offsets back
 * to on-screen rects (VE-20838 keyword-highlight follow-up).
 */
export const SET_PAGE_TEXT_ITEMS = createAction<{
  page: number;
  items: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    length: number;
  }>;
}>('redact/documents/SET_PAGE_TEXT_ITEMS');

/**
 * Bulk result of the pdf pre-pass (VE-25293): one dispatch carrying text,
 * text-item geometry, searchability, and native dimensions for every page.
 * Replaces N × 3 per-page dispatches so populating a long document's page
 * metadata doesn't trigger a dispatch/render storm at open. The pre-pass
 * extracts this without rendering any page, so keyword search / occurrence
 * counts / searchability warnings work across the whole document even though
 * the editor canvas only renders the pages near the viewport. Reducer applies
 * all four maps (pageTexts / pageTextItems / pageSearchability /
 * pageDimensions) in a single pass.
 */
export const SET_PAGE_TEXT_BATCH = createAction<{
  pages: Array<{
    page: number;
    text: string;
    items: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      length: number;
    }>;
    searchability: 'searchable' | 'unsearchable' | 'blank';
    dims: { width: number; height: number };
  }>;
}>('redact/documents/SET_PAGE_TEXT_BATCH');

/**
 * The open-time text pre-pass has finished (VE-26267). Dispatched on completion
 * OR failure of `runPdfPrepass` (not on cancellation: that session is gone or
 * replaced), so `session.textIndexReady` flips true once. `failed`: pages past
 * the failure may have no text, so lexicon runs treat the index as partial.
 */
export const PDF_TEXT_PREPASS_DONE = createAction<
  { failed?: boolean } | undefined
>('redact/documents/PDF_TEXT_PREPASS_DONE');

/**
 * Progress of the streaming text pre-pass (VE-26267): `indexed` of `total` pages
 * extracted so far. Dispatched per streamed chunk to drive the search rail's
 * "Indexing document — X of Y pages" indicator.
 */
export const SET_TEXT_INDEX_PROGRESS = createAction<{
  indexed: number;
  total: number;
}>('redact/documents/SET_TEXT_INDEX_PROGRESS');

/**
 * The scan that tells a SCANNED page from a genuinely blank one has finished.
 *
 * A SECOND pass, and the distinction matters: the text pre-pass above leaves
 * every text-less page provisionally `blank`, because decoding a page just to
 * classify it blocked scanned documents for tens of seconds (VE-27250). Only
 * the raster probe forked after it promotes the ones that actually paint
 * content to `unsearchable`. So `textIndexReady` says the TEXT is indexed; it
 * says nothing about whether we yet know which pages need OCR.
 *
 * Dispatched when the probe pass ends, when there was nothing to probe, and
 * when the pre-pass failed — so it flips once and cannot stick, like
 * `PDF_TEXT_PREPASS_DONE`. No payload.
 */
export const PAGE_SEARCHABILITY_SCAN_DONE = createAction(
  'redact/documents/PAGE_SEARCHABILITY_SCAN_DONE'
);

/**
 * Progress of that scan: `scanned` of `total` text-less pages probed so far.
 *
 * `total` is the text-less pages only, not the document — those are the only
 * ones the probe has anything to decide about. Drives the detections panel's
 * "checking which pages need OCR" row, which is a wait the reviewer is being
 * asked to sit through, so it says how far along it is.
 */
export const SET_SEARCHABILITY_SCAN_PROGRESS = createAction<{
  scanned: number;
  total: number;
}>('redact/documents/SET_SEARCHABILITY_SCAN_PROGRESS');

/**
 * Fast dims-only pre-pass batch (VE-27261). SET_PAGE_TEXT_BATCH above also
 * carries text/searchability, so it only lands once the SLOW whole-document
 * text extraction finishes — far too late for the virtualized viewers, which
 * need accurate per-page reserved heights immediately. Without them a far
 * scroll/jump lays out spacers from DEFAULT_ASPECT, then reflows (and the
 * current-page indicator drifts, e.g. 500 → 387) as each page's real size
 * finally arrives. This action carries ONLY dimensions, streamed in chunks from
 * a cheap getViewport pass, so page heights firm up within a second or two of
 * open. It never writes text/items/searchability (those stay owned by
 * SET_PAGE_TEXT_BATCH), so the two passes can't clobber each other.
 */
export const SET_PAGE_DIMENSIONS_BATCH = createAction<{
  pages: Array<{ page: number; dims: { width: number; height: number } }>;
}>('redact/documents/SET_PAGE_DIMENSIONS_BATCH');

// ---------------------------------------------------------------------------
// Document comments (VE-21982)
//
// Parallels the media-comments action set in `@common-modules/mediaDetails`
// but targets the document-comments registry (see
// `lookupLatestDocumentCommentsSchemaId`). User lookups (`fetchMediaUser`,
// `fetchMediaCommentsUser`) and the global user / users selectors stay on the
// shared mediaDetails pipeline since users are app-wide.
// ---------------------------------------------------------------------------

export const FETCH_DOCUMENT_COMMENTS = createAction<{
  tdoId: TDOId;
  limit: number;
  offset: number;
}>('redact/documents/FETCH_DOCUMENT_COMMENTS');
export const FETCH_DOCUMENT_COMMENTS_SUCCESS =
  createGraphQLSuccessAction<FetchDocumentCommentsResponse>(
    'redact/documents/FETCH_DOCUMENT_COMMENTS_SUCCESS'
  );
export const FETCH_DOCUMENT_COMMENTS_FAILURE = createGraphQLFailureAction(
  'redact/documents/FETCH_DOCUMENT_COMMENTS_FAILURE'
);

export const ADD_NEW_DOCUMENT_COMMENT = createAction<{
  commentId: string;
  searchName: string;
  tdoId: TDOId;
  userId: string;
  /**
   * Optional document anchor. Currently always `undefined` from the panel
   * (placeholder for the anchored-comments UI). When supplied, the comment
   * is pinned to that page/region in the persisted record.
   */
  page?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}>('redact/documents/ADD_NEW_DOCUMENT_COMMENT');

export const UPDATE_DOCUMENT_COMMENT = createAction<{
  comment: DocumentComment;
  modify?: boolean;
  isUndo?: boolean;
}>('redact/documents/UPDATE_DOCUMENT_COMMENT');

export const UPDATE_DOCUMENT_COMMENT_SUCCESS = createGraphQLSuccessAction<
  UpdateDocumentCommentResponse & { isUndo?: boolean }
>('redact/documents/UPDATE_DOCUMENT_COMMENT_SUCCESS');

export const UPDATE_DOCUMENT_COMMENT_FAILURE = createGraphQLFailureAction(
  'redact/documents/UPDATE_DOCUMENT_COMMENT_FAILURE'
);

export const REMOVE_DOCUMENT_COMMENT = createAction<{
  commentId: string;
  isCancelledNewComment?: boolean;
}>('redact/documents/REMOVE_DOCUMENT_COMMENT');

export const SELECT_DOCUMENT_COMMENT = createAction<{ commentId: string }>(
  'redact/documents/SELECT_DOCUMENT_COMMENT'
);

/**
 * Sets which document-comment is currently being edited (or `null` to clear).
 * Mirrors the panel's "edit row" UI state in redux so the right-click
 * "Add Comment" menu can drop a freshly-created comment into edit mode
 * without holding a ref to the panel.
 */
export const SET_EDITING_DOCUMENT_COMMENT = createAction<{
  commentId: string | null;
}>('redact/documents/SET_EDITING_DOCUMENT_COMMENT');

export const SET_DOCUMENT_COMMENT_DRAFT = createAction<{
  commentId: string;
  comment: string;
}>('redact/documents/SET_DOCUMENT_COMMENT_DRAFT');

export const CLEAR_DOCUMENT_COMMENT_DRAFT = createAction<{
  commentId: string;
}>('redact/documents/CLEAR_DOCUMENT_COMMENT_DRAFT');

/**
 * Sets the "focused" comment — used by the floating comment icons on the
 * page. The Comment row in the panel watches this id; when it matches its
 * own commentId, it scrolls itself into view and then clears the focus.
 */
export const FOCUS_DOCUMENT_COMMENT = createAction<{
  commentId: string | null;
}>('redact/documents/FOCUS_DOCUMENT_COMMENT');

// ---------------------------------------------------------------------------
// Custom lexicons (VE-24948 / U5)
// ---------------------------------------------------------------------------

/**
 * Lexicons the user picked in the upload dialog (`U5-FR14`).
 *
 * Persisted, NOT evaluated (BR-49): matching consumes extracted page text,
 * which does not exist until the editor opens the document. Evaluation runs at
 * first open (BR-50), which is why the upload copy must say "will be applied"
 * rather than "applied".
 *
 * Dispatched by the upload selector and consumed by the upload flow, both of
 * which are Step 7 of the code-generation plan. Declared here with the rest of
 * the lexicon contract so the two halves cannot drift.
 */
export const SELECT_LEXICONS_FOR_UPLOAD = createAction<{
  lexiconIds: readonly string[];
  /**
   * Which of them run with AUTO REDACT — matches promoted straight to
   * redactions at first open instead of arriving as detections to review.
   *
   * Always sent together with `lexiconIds` and derived from the same state, so
   * the two cannot disagree about a lexicon that is no longer selected.
   */
  autoRedactLexiconIds?: readonly string[];
}>('redact/documents/SELECT_LEXICONS_FOR_UPLOAD');

/**
 * Snapshots the current upload selection against ONE batch's files, keyed by
 * their upload URLs, and clears the live selection (`U5-FR14`).
 *
 * Dispatched by the overview saga when a picker batch finishes uploading,
 * BEFORE it creates the TDOs. Scoping the selection to the batch is what lets
 * EVERY PDF of a multi-file upload receive it while a TDO created outside the
 * batch — a later upload, or the URL-ingest path — cannot inherit it
 * (review-gate finding 2).
 */
export const STAGE_UPLOAD_LEXICON_BATCH = createAction<{
  fileUrls: readonly string[];
}>('redact/documents/STAGE_UPLOAD_LEXICON_BATCH');

/**
 * Marks one staged file's selection as recorded, releasing its URL from the
 * batch. Dispatched by `recordUploadLexiconSelection` after a successful save;
 * the batch clears itself once every URL is consumed.
 */
export const CONSUME_UPLOAD_LEXICON_BATCH_FILE = createAction<{
  fileUrl: string;
}>('redact/documents/CONSUME_UPLOAD_LEXICON_BATCH_FILE');

/**
 * Apply lexicons to the open document (`U5-FR18`), or evaluate an
 * upload-selected set at first open (BR-50).
 *
 * `source` records which entry point this run belongs to; BR-51 requires it
 * because `U5-FR16`'s audit trail distinguishes them.
 */
export const APPLY_LEXICONS_TO_OPEN_DOCUMENT = createAction<{
  lexiconIds: readonly string[];
  source: 'upload' | 'editor' | 'auto';
}>('redact/documents/APPLY_LEXICONS_TO_OPEN_DOCUMENT');

/** A run started. `requestId` correlates its progress and outcome. */
export const LEXICON_APPLY_STARTED = createAction<{
  requestId: string;
  lexiconIds: readonly string[];
  /**
   * This run redacts what it finds, so the editor blocks on it (VE-24948).
   *
   * Only the FIRST-OPEN evaluation of an auto-redacting selection sets it. An
   * ordinary apply streams into the panel and must not take the document away
   * from the reviewer; this one is about to change the document without being
   * asked, which is the case worth interrupting for.
   */
  autoRedact?: boolean;
}>('redact/documents/LEXICON_APPLY_STARTED');

/**
 * Call off the AUTO REDACTION, not the search (VE-24948).
 *
 * The matching finishes and its matches still arrive as detections to review;
 * only the promotion to redactions is abandoned. Stopping the search would
 * throw away work that has to be redone on the next open, in order to decline
 * something the reviewer can equally decline afterwards.
 */
export const CANCEL_LEXICON_AUTO_REDACT = createAction(
  'redact/documents/CANCEL_LEXICON_AUTO_REDACT'
);

/** The reviewer has read the "N redactions were applied" summary. */
export const ACKNOWLEDGE_LEXICON_AUTO_REDACT = createAction(
  'redact/documents/ACKNOWLEDGE_LEXICON_AUTO_REDACT'
);

/**
 * Un-redact everything AUTO REDACT redacted, from the summary dialog.
 *
 * Works off `autoRedactedMatchIds`, which is why that is recorded separately
 * from `redactedMatchIds`: the reviewer's own redactions are not in it and
 * must not be touched.
 */
export const REMOVE_LEXICON_AUTO_REDACTIONS = createAction(
  'redact/documents/REMOVE_LEXICON_AUTO_REDACTIONS'
);

/**
 * A page reported matches (`NFR-U5-5`: the panel streams rather than blocking
 * on the whole run).
 */
export const LEXICON_APPLY_PROGRESS = createAction<{
  requestId: string;
  pages: readonly number[];
  matchCount: number;
}>('redact/documents/LEXICON_APPLY_PROGRESS');

/**
 * The selection is recorded but there is no extracted page text to match yet.
 *
 * A distinct outcome from "0 matches", deliberately: reporting zero over a
 * document whose text has not been extracted is a defect (BR-48), and the user
 * would read it as "my lexicon does not work".
 */
export const LEXICON_APPLY_AWAITING_TEXT = createAction<{
  requestId: string;
}>('redact/documents/LEXICON_APPLY_AWAITING_TEXT');

/**
 * A run finished. Carries the merged detection list rather than the raw
 * matches: the D13 merge is a pure function over the session
 * (`mergeLexiconMatches`), and dispatching its RESULT keeps the reducer free of
 * merge policy and makes the whole matrix testable without a store.
 */
export const LEXICON_APPLY_SUCCESS = createAction<{
  requestId: string;
  detections: readonly Detection[];
  /** How many matches this run redacted automatically (VE-24948). */
  autoRedactedCount?: number;
  /** WHICH matches those are — what "Remove Auto Redaction" undoes. */
  autoRedactedMatchIds?: readonly string[];
  appliedLexicons: readonly AppliedLexiconRecord[];
  matchCount: number;
  /** Entries the matcher abandoned — BR-47 requires reporting them to the user. */
  abandonedEntryIds: readonly string[];
  /**
   * Which lexicons those abandoned entries belong to, by name.
   *
   * The panel reports the COUNT from `abandonedEntryIds` and the WHERE from
   * this. Entry ids are meaningless to a reviewer (they appear on no other
   * surface in the product) and entry terms are forbidden here by
   * NFR-S7 / SEC-R3, so the lexicon name is the one identifier that is both
   * resolvable and permitted.
   *
   * May be SHORTER than the entry count, or empty while the count is not: an
   * unattributed entry (`disposition.unattributed`) by definition belongs to no
   * supplied lexicon, so there is no name to give for it. Optional so
   * dispatchers and fixtures that predate it still type-check.
   */
  abandonedLexiconNames?: readonly string[];
  timedOut: boolean;
  /**
   * The matching worker DIED on at least one page (review-gate #3 BR-12).
   *
   * Separate from `timedOut` because the two need different sentences and
   * different remedies. Optional so existing dispatchers and fixtures that
   * predate it still type-check as "did not crash".
   */
  crashed?: boolean;
  pagesMatched: readonly number[];
  /**
   * Pages that went unsearched — the deadline elapsed, or the worker died.
   *
   * The reviewer's actionable fact: which page to look at. Page NUMBERS only,
   * never text (NFR-S7 / SEC-R3). Review-gate #3 BR-11.
   */
  unsearchedPages?: readonly number[];
  /** Read before the index finished; unindexed pages are on the records only. */
  indexing?: boolean;
  /**
   * The text index FAILED before its page count was known, so the pages this
   * run missed cannot be listed (VE-29686). Without it a run over a document
   * indexed to page 1 reported an empty `unsearchedPages`, and the panel and
   * the audit trail both read clean. Only on a failed index: while streaming,
   * `indexing` already says the rest is still to come.
   */
  pageCountUnknown?: boolean;
  /**
   * The text index FAILED and left a gap this run could not search (VE-29686).
   *
   * Distinct from `pageCountUnknown`, which is the narrower case where the
   * count was never learned so the pages cannot be listed. The common shape —
   * an index that dies PART-WAY — has a known count and a listed tail, and
   * without this the banner showed those pages with no remedy and the audit
   * trail recorded them with no cause.
   */
  indexFailed?: boolean;
  /** Lexicons whose `indexing` mark this run cleared (clean or not). */
  completedIndexing?: readonly string[];
  /**
   * Whether this run actually ADDED anything.
   *
   * Re-evaluation fires on every page-text arrival, so most runs on a long
   * document are provable no-ops. Treating those like a real apply pushed an
   * undo snapshot, marked the session dirty and emitted an audit event each
   * time — silently breaking undo, making one user action read as N
   * applications in the disclosure CSV, and autosaving byte-identical content
   * (review finding 7).
   */
  changed: boolean;
  /**
   * Where the matches went, when `matchCount` is lower than what was found.
   *
   * `matchCount` reports what this run put on a card. That is the honest number
   * for the list, but on its own a 0 is ambiguous — nothing matched, everything
   * was already applied, and everything was adopted by M2 all read the same,
   * and the first of those is alarming while the others are routine. The panel
   * needs the breakdown to say which happened.
   */
  disposition?: {
    alreadyPresent: number;
    folded: number;
    dismissed: number;
    unattributed: number;
    /** Applied lexicons that hold no entries at all (BR-4) — a COUNT only. */
    emptyLexicons: number;
    /** Matches found but not placeable — a defect, not a document fact. */
    unresolvedGeometry: number;
  };
}>('redact/documents/LEXICON_APPLY_SUCCESS');

export const LEXICON_APPLY_FAILURE = createAction<{
  requestId: string;
  /**
   * EITHER a `LexiconApplyErrorCode` — which the panel maps to a sentence and
   * a specific remedy — OR an untrusted diagnostic string (a caught
   * `error.message`, a matcher failure) that no surface may render.
   *
   * Deliberately NOT narrowed to the code union (VE-29686 review): the
   * free-form case is real, and the panel's fallthrough to the generic
   * "Applying lexicons failed. Try again." is what keeps regex-engine text —
   * which is not guaranteed free of pattern source (NFR-S7 / SEC-R3) — off the
   * screen and out of the audit trail. Dispatch sites that mean a CODE say
   * `satisfies LexiconApplyErrorCode`, so a typo there is still a type error.
   */
  message: string;
}>('redact/documents/LEXICON_APPLY_FAILURE');

// Convenience action creators matching the call-site ergonomics of the
// media-comments helpers (so panel code reads identically).
export const fetchDocumentComments = (payload: {
  tdoId: TDOId;
  limit: number;
  offset: number;
}) => FETCH_DOCUMENT_COMMENTS(payload);

export const addNewDocumentComment = (
  payload: Parameters<typeof ADD_NEW_DOCUMENT_COMMENT>[0]
) => ADD_NEW_DOCUMENT_COMMENT(payload);

export const updateDocumentComment = (payload: {
  comment: DocumentComment;
  modify?: boolean;
  isUndo?: boolean;
}) => UPDATE_DOCUMENT_COMMENT(payload);

export const removeDocumentComment = (
  commentId: string,
  isCancelledNewComment?: boolean
) => REMOVE_DOCUMENT_COMMENT({ commentId, isCancelledNewComment });

export const selectDocumentComment = (commentId: string) =>
  SELECT_DOCUMENT_COMMENT({ commentId });

export const setEditingDocumentComment = (commentId: string | null) =>
  SET_EDITING_DOCUMENT_COMMENT({ commentId });

export const setDocumentCommentDraft = (commentId: string, comment: string) =>
  SET_DOCUMENT_COMMENT_DRAFT({ commentId, comment });

export const clearDocumentCommentDraft = (commentId: string) =>
  CLEAR_DOCUMENT_COMMENT_DRAFT({ commentId });

export const focusDocumentComment = (commentId: string | null) =>
  FOCUS_DOCUMENT_COMMENT({ commentId });
